import { useCallback, useMemo, useRef, useState } from 'react'
import { Wheel } from 'react-custom-roulette'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'

import { Button } from '@/common/ui/button'
import useMutationSpinLuckyWheel from '@/modules/promotion/api/hooks/useMutationSpinLuckyWheel'

import { LuckyWheelQueryKeys } from '@/modules/promotion/keys'

import type { GetLuckyWheelResponse } from '@/modules/promotion/type'

interface LuckyWheelFallbackProps {
	wheelData: GetLuckyWheelResponse
	disabled?: boolean
	onSpinComplete?: () => void
}

export default function LuckyWheelFallback({
	wheelData,
	disabled,
	onSpinComplete,
}: LuckyWheelFallbackProps) {
	const queryClient = useQueryClient()
	const [mustSpin, setMustSpin] = useState(false)
	const [prizeNumber, setPrizeNumber] = useState(0)
	const [isSpinning, setIsSpinning] = useState(false)

	const segments = useMemo(() => {
		return wheelData.wheelSetting || []
	}, [wheelData.wheelSetting])

	const lastResultRef = useRef<{
		title: string
		amount: number
		type: string
		color: string
	} | null>(null)

	// Convert segments to wheel data format
	const wheelDataFormatted = useMemo(() => {
		return segments.map((segment, index) => ({
			option: `${segment.amount}`,
			style: {
				backgroundColor:
					segment.color || `hsl(${(index * 360) / segments.length}, 70%, 60%)`,
				textColor: '#ffffff',
				fontSize: 16,
			},
			optionSize: 1,
		}))
	}, [segments])

	const { mutate: spin, isPending } = useMutationSpinLuckyWheel({
		onSuccess: (data) => {
			const result = data?.luckyWheelSpinResult
			if (result) {
				lastResultRef.current = {
					title: result.title,
					amount: result.amount,
					type: result.type,
					color: result.color,
				}

				const winnerIndex = segments.findIndex(
					(s) =>
						s.title === result.title &&
						s.amount === result.amount &&
						s.type === result.type,
				)

				if (winnerIndex >= 0) {
					setPrizeNumber(winnerIndex)
					setMustSpin(true)
				} else {
					// Fallback: random segment
					setPrizeNumber(Math.floor(Math.random() * segments.length))
					setMustSpin(true)
				}
			}
			queryClient.invalidateQueries({
				queryKey: [LuckyWheelQueryKeys.GetLuckyWheel],
			})
		},
		onError: (error: Error) => {
			showErrorToast(error || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
			setIsSpinning(false)
		},
	})

	const handleSpin = useCallback(() => {
		if (isSpinning || isPending || mustSpin || disabled) return

		setIsSpinning(true)
		lastResultRef.current = null

		// Trigger backend spin
		spin()
	}, [isPending, isSpinning, mustSpin, disabled, spin])

	const handleSpinComplete = useCallback(() => {
		setMustSpin(false)
		setIsSpinning(false)

		const result = lastResultRef.current
		if (result) {
			toast.success(`${result.title} (${result.amount})`)
		}

		onSpinComplete?.()
	}, [onSpinComplete])

	if (segments.length === 0) {
		return (
			<div className="flex flex-col items-center gap-4">
				<div className="flex h-64 w-64 items-center justify-center rounded-full bg-gray-100">
					<p className="text-gray-500">ไม่มีข้อมูลรางวัล</p>
				</div>
			</div>
		)
	}

	return (
		<div className="flex flex-col items-center gap-4">
			<div className="[&>div]:max-h-[280px] [&>div]:max-w-[280px]">
				<Wheel
					mustStartSpinning={mustSpin}
					prizeNumber={prizeNumber}
					data={wheelDataFormatted}
					onStopSpinning={handleSpinComplete}
					spinDuration={0.2}
					outerBorderColor="#888"
					outerBorderWidth={8}
					innerBorderColor="#888"
					innerBorderWidth={30}
					radiusLineWidth={0}
					fontSize={30}
					textDistance={60}
					backgroundColors={wheelDataFormatted.map(
						(prize) => prize.style.backgroundColor,
					)}
					pointerProps={{
						style: { visibility: 'visible' },
					}}
				/>
			</div>

			{/* Remaining attempts display */}
			{wheelData.remainingAttempts !== undefined && (
				<div className="text-center text-sm text-muted-foreground">
					เหลือโควตา:{' '}
					<span className="font-medium text-primary">
						{wheelData.remainingAttempts}
					</span>{' '}
					ครั้ง
				</div>
			)}

			<Button
				onClick={handleSpin}
				disabled={isSpinning || isPending || mustSpin || disabled}
				className="h-14 min-w-40 text-2xl font-bold"
			>
				{isSpinning || isPending || mustSpin ? 'กำลังหมุน...' : 'หมุน!'}
			</Button>
		</div>
	)
}
